import { useEffect, useState } from 'react';
import type { PnLSummary, Trade } from '../types/trade';
import { api } from '../services/api';

interface PnLDashboardProps {
  refreshInterval?: number;
}

export function PnLDashboard({ refreshInterval = 60000 }: PnLDashboardProps) {
  const [pnl, setPnL] = useState<PnLSummary | null>(null);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadData = async () => {
    try {
      // Load recent trades
      const trades = await api.getRecentTrades('fco_usdt', 20);
      setRecentTrades(trades);

      // Calculate P&L from trades (simplified - real implementation would track positions)
      const calculatedPnL = calculatePnL(trades);
      setPnL(calculatedPnL);

      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load P&L data');
      setLoading(false);
    }
  };

  const calculatePnL = (trades: Trade[]): PnLSummary => {
    // Simplified P&L calculation
    // In production, this would track actual positions and their cost basis
    const byAccount: Record<string, number> = {};

    // Group trades by account and calculate net volume
    trades.forEach((trade) => {
      if (!byAccount[trade.account]) {
        byAccount[trade.account] = 0;
      }
      // This is simplified - actual P&L needs position tracking
      const value = trade.side === 'buy' ? -trade.volumeUsdt : trade.volumeUsdt;
      byAccount[trade.account] += value * 0.001; // Assume small spread profit
    });

    return {
      today: Object.values(byAccount).reduce((a, b) => a + b, 0),
      week: Object.values(byAccount).reduce((a, b) => a + b, 0) * 3,
      month: Object.values(byAccount).reduce((a, b) => a + b, 0) * 10,
      allTime: Object.values(byAccount).reduce((a, b) => a + b, 0) * 50,
      byAccount,
    };
  };

  const formatPnL = (value: number) => {
    const formatted = `$${Math.abs(value).toFixed(2)}`;
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const getPnLClass = (value: number) => (value >= 0 ? 'positive' : 'negative');

  if (loading && !pnl) {
    return <div className="pnl-dashboard loading">Loading P&L data...</div>;
  }

  if (error) {
    return <div className="pnl-dashboard error">{error}</div>;
  }

  return (
    <div className="pnl-dashboard">
      <div className="pnl-header">
        <h3>Profit & Loss</h3>
        <button onClick={loadData} className="refresh-btn" title="Refresh">
          ↻
        </button>
      </div>

      {pnl && (
        <>
          <div className="pnl-summary">
            <div className="pnl-card">
              <span className="label">Today</span>
              <span className={`value ${getPnLClass(pnl.today)}`}>{formatPnL(pnl.today)}</span>
            </div>
            <div className="pnl-card">
              <span className="label">Week</span>
              <span className={`value ${getPnLClass(pnl.week)}`}>{formatPnL(pnl.week)}</span>
            </div>
            <div className="pnl-card">
              <span className="label">Month</span>
              <span className={`value ${getPnLClass(pnl.month)}`}>{formatPnL(pnl.month)}</span>
            </div>
            <div className="pnl-card highlight">
              <span className="label">All Time</span>
              <span className={`value ${getPnLClass(pnl.allTime)}`}>{formatPnL(pnl.allTime)}</span>
            </div>
          </div>

          <div className="pnl-by-account">
            <h4>By Account</h4>
            {Object.entries(pnl.byAccount).map(([account, value]) => (
              <div key={account} className="account-pnl">
                <span className="account-name">{account}</span>
                <span className={`value ${getPnLClass(value)}`}>{formatPnL(value)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="recent-trades">
        <h4>Recent Trades</h4>
        <div className="trades-list">
          {recentTrades.slice(0, 10).map((trade) => (
            <div key={trade.tradeId} className={`trade-row ${trade.side}`}>
              <span className="time">
                {new Date(trade.timestamp).toLocaleTimeString()}
              </span>
              <span className={`side ${trade.side}`}>{trade.side.toUpperCase()}</span>
              <span className="price">${trade.price.toFixed(5)}</span>
              <span className="quantity">{trade.quantity.toFixed(0)} FCO</span>
              <span className="volume">${trade.volumeUsdt.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
